import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { capabilitiesData } from "./CapabilitiesData";
import DetectionAnimation from "./DetectionAnimation";
import "../components/StandardPage.jsx";
import "../../css/components/standardPage.css";
import "../../css/capabilities/capabilitiesPage.css";

const CapabilitySection = ({ id, data, sectionName, showDetectionAnimation }) => {
  const scrollToNext = () => {
    const sections = ["detecting", "capturing"];
    const currentIndex = sections.indexOf(id);
    if (currentIndex < sections.length - 1) {
      const nextSection = document.getElementById(sections[currentIndex + 1]);
      const navbarElement = document.getElementsByClassName("header")[0];
      if (nextSection && navbarElement) {
        const sectionTop = nextSection.offsetTop;
        const navbarHeight = navbarElement.offsetHeight;
        window.scrollTo({
          top: sectionTop - navbarHeight,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <div id={id} className="capability-section-wrapper">
      {/* Title Banner - optional, appears above hero section */}
      {data.titleAbove && (
        <div className="title-banner">
          <div className="title-banner-particles">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="particle"></div>
            ))}
          </div>
          <h1 className="page-title">
            <span className="section-prefix">{sectionName}</span>
          </h1>
        </div>
      )}
      
      {/* Hero Section with background - contains header and smaller cards */}
      <div
        className={`page-hero ${data.cardsPosition === 'bottom' ? 'cards-bottom' : ''} ${data.content[0]?.tiltShift ? 'tilt-shift-section' : ''}`}
        style={{
          backgroundImage: data.content[0]?.href ? `url(${data.content[0].href})` : undefined,
        }}
      >
        <div className="hero-overlay"></div>
        
        {/* Detection animation for Detect section only */}
        {showDetectionAnimation && <DetectionAnimation />}
        
        {/* Title - show if titleAbove is true (moves inside hero section) or if titleAbove is false (original behavior) */}
        {data.titleAbove ? (
          <h1 className="page-title" style={{ marginBottom: '2rem' }}>
            {data.title}
          </h1>
        ) : (
        <h1 className="page-title">
          <span className="section-prefix">{sectionName}:</span> {data.title}
        </h1>
        )}
        
        {/* Hero Cards - smaller cards with key points */}
        <div className="hero-cards-container">
          {data.heroCards.map((card, index) => (
            <div key={index} className="hero-card">
              <h3 className="hero-card-title">{card.title}</h3>
              <p className="hero-card-description">{card.description}</p>
            </div>
          ))}
        </div>
        
        {/* <button
          className="scroll-arrow"
          onClick={scrollToNext}
          aria-label="Scroll to next section"
        >
          <ChevronDown className="arrow-icon" size={32} strokeWidth={3} />
        </button> */}
      </div>

      {/* Content Sections */}
      <div className="content-sections">
        {data.content.slice(1).map((section, index) => (
          <div
            key={index}
            className={`content-section ${section.cardsPosition === 'bottom' ? 'cards-bottom' : ''} ${section.tiltShift ? 'tilt-shift-section' : ''}`}
            style={{
              backgroundImage: section.href ? `url(${section.href})` : undefined,
            }}
          >
            <div className="section-overlay"></div>
            {section.title && <h2 className="section-title-overlay">{section.title}</h2>}
            <div className={`section-cards-container ${section.cards.length === 2 ? 'two-cards' : section.cards.length === 3 ? 'three-cards' : ''}`}>
              {section.cards.map((card, cardIndex) => (
                <div key={cardIndex} className="section-card">
                  <p className="section-card-description">{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CapabilitiesPage = () => {
  const location = useLocation();
  const [scrolledToHash, setScrolledToHash] = useState(false);

  useEffect(() => {
    // Scroll to hash on initial load or when hash changes
    if (location.hash && !scrolledToHash) {
      setTimeout(() => {
        const id = location.hash.replace("#", "");
        const element = document.getElementById(id);
        const navbarElement = document.getElementsByClassName("header")[0];
        
        if (element && navbarElement) {
          const elementTop = element.offsetTop;
          const navbarHeight = navbarElement.offsetHeight;
          window.scrollTo({
            top: elementTop - navbarHeight,
            behavior: "smooth",
          });
          setScrolledToHash(true);
        }
      }, 100);
    }
  }, [location.hash, scrolledToHash]);

  // Reset scrolledToHash when hash changes
  useEffect(() => {
    setScrolledToHash(false);
  }, [location.hash]);

  return (
    <div className="standard-page">
      <CapabilitySection id="detecting" data={capabilitiesData.detecting} sectionName="Detect" showDetectionAnimation={true} />
      <CapabilitySection id="capturing" data={capabilitiesData.capturing} sectionName="Capture" />
      
      {/* Transition Section to R&D */}
      <div className="capabilities-transition-section">
        <div className="transition-content">
          <h2 className="transition-title">
            Space-Demonstrated Technologies, <span className="highlight-text">Today</span>
          </h2>
          <p className="transition-description">
            Our detection and capture capabilities aren't just concepts—they're proven, operational technologies 
            delivering results right now. Our Sutter telescopes are actively tracking satellites and asteroids 
            for the Space Force and NASA, and our Capture Bag technology was successfully deployed on the International Space 
            Station in October 2025.
          </p>
          <div className="transition-divider"></div>
          <p className="transition-subtext">
            These capabilities are just the beginning of our journey to make asteroid mining a reality.
          </p>
          <Link to="/rnd#moving" className="rnd-cta-button">
            Explore Our R&D Roadmap
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CapabilitiesPage;
